// Refactored userActions.js using secureFetch
import { BASE_URL } from '../../constants';
import { secureFetch } from '../../utils/secureFetch';

export const fetchAllUsers = (condition) => async (dispatch, getState) => {
  try {
    const params = new URLSearchParams({
      page: condition.page ?? 1,
      limit: condition.limit ?? 10,
      search: condition.search ?? '',
    });
    if (condition.role && condition.role !== 'all') params.set('role', condition.role);
    if (condition.status && condition.status !== 'all') params.set('status', condition.status);
    if (condition.cohort) params.set('cohort', condition.cohort);
    if (Array.isArray(condition.leagues) && condition.leagues.length) {
      params.set('leagues', condition.leagues.join(','));
    }
    if (condition.team_id) params.set('team_id', condition.team_id);
    const data = await secureFetch({
      method: 'GET',
      url: `${BASE_URL}/api/users/users/all/?${params.toString()}`,
      getState,
    });
    dispatch({
      type: 'FETCH_ALL_USERS',
      payload: data.users,
      totalCounts: data.total_counts,
      bucketCounts: data.bucket_counts || null,
    });
  } catch (err) {
    console.error('fetchAllUsers error:', err);
  }
};

export const PERMISSION_SAVE_FAILED_MESSAGE = 'Could not change the role. Try again.';

// Rethrows a refused change as an Error carrying the server's words (its
// `message`, else an `error` or `detail` sentence, never a bare code), such
// as "Change roles from your own admin session.", so the Users page can
// show it.
export const updateUserPermission = (user) => async (dispatch, getState) => {
  try {
    const data = await secureFetch({ method: 'PUT', url: `${BASE_URL}/api/users/users/permission/update/`, body: user, getState });
    dispatch({ type: 'UPDATE_USER', payload: data });
    return data;
  } catch (err) {
    console.error('updateUserPermission error:', err);
    const { body } = parseHttpError(err);
    const text = (v) => (typeof v === 'string' ? v.trim() : '');
    const sentence = [text(body?.error), text(body?.detail)].find((v) => v && !/^[a-z0-9_.-]+$/.test(v));
    throw new Error(text(body?.message) || sentence || PERMISSION_SAVE_FAILED_MESSAGE);
  }
};

export const clearAllUsers = () => ({
  type: 'CLEAR_ALL_USERS',
});

// Toggle an admin's "email me on athlete/coach signup" preference.
export const toggleAdminSignupNotify = (email, enabled) => async (_dispatch, getState) => {
  return secureFetch({
    method: 'POST',
    url: `${BASE_URL}/api/users/admin/users/${encodeURIComponent(email)}/notify-signup/`,
    body: { enabled },
    getState,
  });
};

// Invite a new admin by email. Backend creates the user with IS_ADMIN +
// `mustChangePassword=true` and emails a temp password. Returns the
// created email + the one-time temp password (so the inviter can copy it
// from the success toast even if email delivery lags).
export const inviteAdmin = (payload) => async (_dispatch, getState) => {
  const res = await secureFetch({
    method: 'POST',
    url: `${BASE_URL}/api/users/admin/invite-admin/`,
    body: payload,
    getState,
  });
  return res;
};

// secureFetch throws `HTTP <status>: <body>`; read the status and the JSON
// body back out of it. status is 0 for a network failure.
export const parseHttpError = (err) => {
  const m = /^HTTP (\d{3}): ([\s\S]*)$/.exec(err?.message || '');
  if (!m) return { status: 0, body: null };
  let body = null;
  try { body = JSON.parse(m[2]); } catch (e) { body = null; }
  return { status: Number(m[1]), body };
};

// The server refused the session: a locked, deleted or expired account.
export const SESSION_ENDED_EVENT = 'cache:session-ended';
export const SESSION_ENDED_MESSAGE = 'Your session has ended. Log in again.';
export const ACCOUNT_MISSING_MESSAGE = 'We could not find your account. Log in again.';

// The plain message to show: the server's own `message` (the lock notice)
// when it sent one, never a raw code such as account_locked.
export const sessionEndedMessage = (status, body) => {
  const msg = body && typeof body.message === 'string' ? body.message.trim() : '';
  if (msg) return msg;
  return status === 404 ? ACCOUNT_MISSING_MESSAGE : SESSION_ENDED_MESSAGE;
};

// Tell the app to sign out and show `message` on the login page. The
// listener lives in useAuthSync. `cleared` means the caller already ran
// logout().
export const announceSessionEnded = (message, { cleared = false } = {}) => {
  try {
    window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT, {
      detail: { message: message || SESSION_ENDED_MESSAGE, cleared },
    }));
  } catch (e) { /* no window (tests, SSR) */ }
};

export const USER_LOAD_FAILED_MESSAGE = 'We could not load your account. Check your connection and try again.';

export const fetchUserByEmail = (email) => async (dispatch, getState) => {
  const token = getState().auth?.token;
  // Another sign-in (or a log out) happened while this was in flight, so
  // the answer belongs to a session that is gone.
  const stale = () => !!token && getState().auth?.token !== token;
  try {
    const data = await secureFetch({ method: 'GET', url: `${BASE_URL}/api/users/users/by-email/?email=${encodeURIComponent(email)}`, getState });
    if (stale()) return;
    dispatch({ type: 'FETCH_USER_BY_EMAIL', payload: data });
  } catch (err) {
    if (stale()) return;
    const { status, body } = parseHttpError(err);
    // 401: the account is locked or the token is dead. 404: the account is
    // gone. Either way the session cannot continue, so sign out instead of
    // leaving the route guards on "Loading..." for good.
    if (token && (status === 401 || status === 404)) {
      dispatch(logout());
      announceSessionEnded(sessionEndedMessage(status, body), { cleared: true });
      return;
    }
    dispatch({ type: 'FETCH_USER_FAILED', payload: USER_LOAD_FAILED_MESSAGE });
    console.error('fetchUserByEmail error:', err);
  }
};

export const fetchUsersByRole = (condition) => async (dispatch, getState) => {
  try {
    let url = `${BASE_URL}/api/users/users/role/?page=${condition['page']}&limit=${condition['limit']}`;
    if (condition['email']) url += `&email=${encodeURIComponent(condition['email'])}`;
    if (condition['search']) url += `&search=${encodeURIComponent(condition['search'])}`;
    const data = await secureFetch({ method: 'GET', url, getState });
    dispatch({ type: 'FETCH_USERS_BY_ROLE', payload: data.users, totalCounts: data.total_counts });
  } catch (err) {
    console.error('fetchUsersByRole error:', err);
  }
};

export const clearUsersByRole = () => ({
  type: 'CLEAR_USERS_BY_ROLE',
});

export const logout = () => (dispatch) => {
  // The session keys, plus the redux-persist blob that holds the JWT. The
  // USER_LOGOUT reset below writes it back with no token.
  const keysToRemove = ['token', 'myEmail', 'permissions', 'expiresAt', 'athleteEmail', 'persist:root'];
  keysToRemove.forEach(localStorage.removeItem.bind(localStorage));
  
  dispatch({ type: 'USER_LOGOUT' });
};

export const fetchLogs = (condition) => async (dispatch, getState) => {
  try {
    // Encoded, so a search holding &, + or # reaches the server intact.
    const query = new URLSearchParams({
      page: String(condition.page ?? ''),
      limit: String(condition.limit ?? ''),
      search: String(condition.search ?? ''),
    });
    const data = await secureFetch({ method: 'GET', url: `${BASE_URL}/api/users/logs/?${query}`, getState });
    dispatch({ type: 'FETCH_LOGS', payload: data.logs, totalCounts: data.total_counts });
  } catch (err) {
    console.error('fetchLogs error:', err);
  }
};

export const fetchUsers = () => async (dispatch, getState) => {
  try {
    const data = await secureFetch({ method: 'GET', url: `${BASE_URL}/api/users/users/whole/`, getState });
    dispatch({ type: 'FETCH_WHOLE_USERS', payload: data.users });
  } catch (err) {
    console.error('fetchAllUsers error:', err);
  }
};

export const fetchAnalytics = () => async (dispatch, getState) => {
  try {
    const data = await secureFetch({ method: 'GET', url: `${BASE_URL}/api/users/analytics/`, getState });
    dispatch({ type: 'SET_ANALYTICS', payload: data });
  } catch (err) {
    console.error('fetchAnalytics error:', err);
  }
};

export const fetchPaymentHistory = (condition) => async (dispatch, getState) => {
  try {
    const data = await secureFetch({ method: 'GET', url: `${BASE_URL}/api/users/payments/history/`, getState });
    dispatch({ type: 'FETCH_PAYMENT_HISTORY', payload: data });
  } catch (err) {
    console.error('fetchPaymentHistory error:', err);
  }
};

export const fetchAllUserPaymentHistory = () => async (dispatch, getState) => {
  try {
    const data = await secureFetch({ method: 'GET', url: `${BASE_URL}/api/users/admin/payments/history/`, getState });
    dispatch({ type: 'FETCH_ALL_USER_PAYMENT_HISTORY', payload: data });
  } catch (err) {
    console.error('fetchAllUserPaymentHistory error:', err);
  }
};

export const fetchDependencies = () => async (dispatch, getState) => {
  try {
    const data = await secureFetch({ method: 'GET', url: `${BASE_URL}/api/users/dependencies/`, getState });
    dispatch({ type: 'FETCH_DEPENDENCIES', payload: data });
  } catch (err) {
    console.error('fetchDependencies error:', err);
  }
};

export const createDependency = (dependency) => async (dispatch, getState) => {
  try {
    const data = await secureFetch({ method: 'POST', url: `${BASE_URL}/api/users/dependencies/`, body: dependency, getState });
    dispatch({ type: 'CREATE_DEPENDENCY', payload: data });
  } catch (err) {
    console.error('createDependency error:', err);
  }
};

export const updateDependency = (dependency) => async (dispatch, getState) => {
  try {
    const data = await secureFetch({ method: 'PUT', url: `${BASE_URL}/api/users/dependencies/${dependency._id}/`, body: dependency, getState });
    dispatch({ type: 'UPDATE_DEPENDENCY', payload: data });
  } catch (err) {
    console.error('updateDependency error:', err);
  }
};

export const deleteDependency = (_id) => async (dispatch, getState) => {
  try {
    const data = await secureFetch({ method: 'DELETE', url: `${BASE_URL}/api/users/dependencies/${_id}/`, getState });
    dispatch({ type: 'DELETE_DEPENDENCY', payload: data });
  } catch (err) {
    console.error('deleteDependency error:', err);
  }
};

// Social Score computation (admin-triggered)
export const computeSocialScore = (email) => async (dispatch, getState) => {
  const data = await secureFetch({
    method: 'POST',
    url: `${BASE_URL}/api/users/social-score/compute/`,
    body: { email },
    getState,
  });
  return data;
};

export const computeSocialScoreAll = () => async (dispatch, getState) => {
  const data = await secureFetch({
    method: 'POST',
    url: `${BASE_URL}/api/users/social-score/compute-all/`,
    body: {},
    getState,
  });
  return data;
};

export const fetchBulkScoreProgress = () => async (dispatch, getState) => {
  const data = await secureFetch({
    method: 'GET',
    url: `${BASE_URL}/api/users/social-score/progress/`,
    getState,
  });
  return data;
};
